import { Collapse } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import type { ProcessRead } from "@nens/rana/api-v1";
import { RanaTable, SIZES } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";

import { LibraryCard } from "@/components/library-card";
import { useIconSrc } from "@/hooks/use-icon-src";
import { nameColumnString } from "@/translations";

interface ProcessLibraryCardProps {
  processes: ProcessRead[];
  setProcess: (process: ProcessRead) => void;
}

export function ProcessLibraryCard({ processes, setProcess }: ProcessLibraryCardProps) {
  const intl = useIntl();
  const { limit, offset, page, setPage, setPageSize, searchString, setSearchString } = useTableData(
    {
      initialPage: 1,
      initialPageSize: 10,
    }
  );
  const icon = useIconSrc("job");

  const filteredProcesses = processes.filter((process) =>
    process.name?.toLowerCase().includes(searchString.toLowerCase())
  );
  const numPages = Math.ceil(filteredProcesses.length / limit);
  const visibleProcesses = filteredProcesses.slice(offset, offset + limit);

  return (
    <LibraryCard
      title={intl.formatMessage({
        id: "heading.process_library",
        defaultMessage: "Process library",
      })}
      description={
        <FormattedMessage
          id="processlist.click_here_to_see_other_processes"
          defaultMessage="Click here to see the other processes"
        />
      }
      buttonLabel={<FormattedMessage id="processlist.library" defaultMessage="Library" />}
      modalTitle={intl.formatMessage({
        id: "processlist.other_processes",
        defaultMessage: "Other processes",
      })}
      height={SIZES.xs}
    >
      <RanaTable
        columnDefinitions={[
          {
            width: "table_cell",
            title: nameColumnString(intl),
          },
          {
            width: "table_cell_xl",
            title: intl.formatMessage({
              id: "column.description",
              defaultMessage: "Description",
            }),
          },
        ]}
        tableDataIds={visibleProcesses.map((item) => item.id)}
        emptyElement={
          <RanaTable.Empty icon={icon}>
            <FormattedMessage
              id="emptyElement.process_no_search_results"
              defaultMessage="No processes found with the current search criteria."
            />
          </RanaTable.Empty>
        }
      >
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "search.process",
              defaultMessage: "Find process",
            }),
            searchString,
            setSearchString,
          }}
        />
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {visibleProcesses.map((process) => {
              const { id, shortDescription, name } = process;
              return (
                <RanaTable.Row onClick={() => setProcess(process)} key={id} rowId={id}>
                  <RanaTable.Cell>{name}</RanaTable.Cell>
                  <RanaTable.Cell title={shortDescription + ""}>{shortDescription}</RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>

        <Collapse in={numPages > 1} unmountOnExit>
          <RanaTable.Pagination
            limit={limit}
            setPageSize={setPageSize}
            page={page}
            numPages={numPages}
            setPage={setPage}
          />
        </Collapse>
      </RanaTable>
    </LibraryCard>
  );
}
